import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  TrendingUp, 
  BarChart3, 
  DollarSign, 
  Users, 
  Shield, 
  Zap,
  Construction
} from 'lucide-react'

const Home = () => {
  const { isAuthenticated } = useAuth()

  // Datos simulados para el mercado (estilo Binance)
  const mockMarketData = [
    { symbol: 'BTC/USDT', price: '43,250.00', change: '+2.45', changePercent: '+5.98%' },
    { symbol: 'ETH/USDT', price: '2,680.50', change: '+125.30', changePercent: '+4.91%' },
    { symbol: 'BNB/USDT', price: '315.20', change: '-8.75', changePercent: '-2.70%' },
    { symbol: 'ADA/USDT', price: '0.4850', change: '+0.0125', changePercent: '+2.65%' },
    { symbol: 'SOL/USDT', price: '98.75', change: '+3.25', changePercent: '+3.40%' },
    { symbol: 'DOT/USDT', price: '7.125', change: '-0.145', changePercent: '-1.99%' }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Responsive */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              Tu Panel Financiero Personal
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 text-blue-100 max-w-4xl mx-auto">
              Controla tus gastos, gestiona tu dinero y comparte con tu familia
            </p>
            
            {!isAuthenticated && (
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
                <Link
                  to="/register"
                  className="bg-white text-blue-600 px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors text-center"
                >
                  Comenzar Gratis
                </Link>
                <Link
                  to="/login"
                  className="border-2 border-white text-white px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-white hover:text-blue-600 transition-colors text-center"
                >
                  Iniciar Sesión
                </Link>
              </div>
            )}
            
            {isAuthenticated && (
              <Link
                to="/dashboard"
                className="bg-white text-blue-600 px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors inline-block"
              >
                Ir a Mi Panel
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Market Section - Responsive */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex flex-col sm:flex-row items-center justify-center mb-4">
            <Construction className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500 mr-0 sm:mr-3 mb-2 sm:mb-0" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
              Mercado de Criptomonedas
            </h2>
          </div>
          <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
            Esta sección está en construcción - Próximamente datos en tiempo real
          </p>
        </div>

        {/* Tabla de mercado - Responsive */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
              Vista Previa del Mercado
            </h3>
          </div>
          
          {/* Mobile table */}
          <div className="block sm:hidden">
            <div className="divide-y divide-gray-200">
              {mockMarketData.map((item, index) => (
                <div key={index} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-gray-900">
                      {item.symbol}
                    </div>
                    <div className={`text-right ${
                      item.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <div className="font-mono text-sm">${item.price}</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.changePercent.startsWith('+')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.changePercent}
                      </span>
                    </div>
                  </div>
                  <div className={`text-sm font-mono ${
                    item.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {item.change}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Par
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cambio 24h
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockMarketData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.symbol}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        ${item.price}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-mono ${
                        item.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.change}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.changePercent.startsWith('+')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.changePercent}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-gray-50 px-4 sm:px-6 py-4 text-center">
            <p className="text-sm text-gray-500">
              🚧 Datos simulados - En construcción 🚧
            </p>
          </div>
        </div>
      </div>

      {/* Features Section - Responsive */}
      <div className="bg-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4">
              ¿Por qué elegir FinancePanel?
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">
              Todas las herramientas que necesitas para controlar tus finanzas
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center p-4 sm:p-6">
              <div className="bg-blue-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Control Total
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Visualiza todos tus gastos e ingresos en un solo lugar
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-green-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Gestión Familiar
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Comparte gastos con tu familia de forma segura y privada
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-purple-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Análisis Inteligente
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Obtén insights sobre tus hábitos de gasto y ahorro
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-yellow-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Fácil de Usar
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Interfaz intuitiva para registrar gastos en segundos
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-red-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Seguro y Privado
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Tus datos están protegidos con la máxima seguridad
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-indigo-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4">
                <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Tiempo Real
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Actualizaciones instantáneas de tu balance y gastos
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Responsive */}
      {!isAuthenticated && (
        <div className="bg-gray-800 text-white py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              ¿Listo para tomar el control de tus finanzas?
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Únete a miles de usuarios que ya confían en FinancePanel
            </p>
            <Link
              to="/register"
              className="bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-blue-700 transition-colors inline-block"
            >
              Crear Cuenta Gratis
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home